import { useUser } from '@clerk/clerk-react'
import { useState } from 'react'

export default function Settings() {
  const { user } = useUser()
  const [activeTab, setActiveTab] = useState('system')

  const triggerBotAction = async (action: string) => {
    try {
      const response = await fetch('/api/bot/trigger', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action })
      })

      if (!response.ok) throw new Error('Failed to trigger action')
      
      const result = await response.json()
      alert(`✅ ${result.message}`)
    } catch (err) {
      console.error('Trigger error:', err)
      alert(`❌ Failed to trigger ${action}`)
    }
  }

  return (
    <div style={{ padding: '2rem' }}>
      <h1>⚙️ Settings</h1>
      <p>Configure your Obayito system settings and manage bot operations.</p>

      {/* Tabs */}
      <div style={{ borderBottom: '1px solid #dee2e6', marginBottom: '2rem' }}>
        <div style={{ display: 'flex', gap: '1rem' }}>
          {[
            { id: 'system', label: '🖥️ System', },
            { id: 'bot', label: '🤖 Bot Control' },
            { id: 'notifications', label: '🔔 Notifications' },
            { id: 'account', label: '👤 Account' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                padding: '0.75rem 1rem',
                border: 'none',
                background: activeTab === tab.id ? '#007bff' : 'transparent',
                color: activeTab === tab.id ? 'white' : '#007bff',
                borderRadius: '4px 4px 0 0',
                cursor: 'pointer',
                fontWeight: activeTab === tab.id ? 'bold' : 'normal'
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* System Tab */}
      {activeTab === 'system' && (
        <div style={{ background: 'white', padding: '2rem', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
          <h3>🖥️ System Information</h3>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '2rem', marginTop: '1.5rem' }}>
            <div>
              <h4>Application Status</h4>
              <div style={{ space: '0.5rem' }}>
                <p><strong>Version:</strong> v2.0.0</p>
                <p><strong>Environment:</strong> Production</p>
                <p><strong>Last Updated:</strong> {new Date().toLocaleDateString()}</p>
                <p><strong>Uptime:</strong> 24/7</p>
              </div>
            </div>
            
            <div>
              <h4>Database Status</h4>
              <div style={{ space: '0.5rem' }}>
                <p><span style={{ color: '#28a745' }}>✅</span> PostgreSQL Connected</p>
                <p><span style={{ color: '#28a745' }}>✅</span> Redis Cache Active</p>
                <p><span style={{ color: '#28a745' }}>✅</span> Cloudflare Workers Running</p>
                <p><span style={{ color: '#28a745' }}>✅</span> QStash Scheduler Active</p>
              </div>
            </div>
          </div>

          <div style={{ marginTop: '2rem', padding: '1rem', background: '#f8f9fa', borderRadius: '8px' }}>
            <h4>🔧 System Health</h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginTop: '1rem' }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '2rem', color: '#28a745' }}>99.9%</div>
                <div style={{ fontSize: '0.875rem', color: '#6c757d' }}>Uptime</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '2rem', color: '#007bff' }}>{'< 100ms'}</div>
                <div style={{ fontSize: '0.875rem', color: '#6c757d' }}>Response Time</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '2rem', color: '#28a745' }}>Active</div>
                <div style={{ fontSize: '0.875rem', color: '#6c757d' }}>Bot Status</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bot Control Tab */}
      {activeTab === 'bot' && (
        <div style={{ background: 'white', padding: '2rem', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
          <h3>🤖 Bot Control Panel</h3>
          <p style={{ color: '#6c757d', marginBottom: '2rem' }}>
            Manually trigger bot operations and manage automated tasks.
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '2rem' }}>
            {/* Daily Operations */}
            <div style={{ padding: '1.5rem', border: '1px solid #dee2e6', borderRadius: '8px' }}>
              <h4 style={{ color: '#007bff', marginBottom: '1rem' }}>📅 Daily Operations</h4>
              
              <button
                onClick={() => triggerBotAction('daily-pipeline')}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  marginBottom: '0.5rem',
                  background: '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                🔄 Run Daily Pipeline
              </button>
              
              <button
                onClick={() => triggerBotAction('order-monitor')}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  marginBottom: '0.5rem',
                  background: '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                📦 Check New Orders
              </button>
              
              <button
                onClick={() => triggerBotAction('cancelled-orders')}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: '#dc3545',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                ❌ Check Cancelled Orders
              </button>
            </div>

            {/* Reports */}
            <div style={{ padding: '1.5rem', border: '1px solid #dee2e6', borderRadius: '8px' }}>
              <h4 style={{ color: '#28a745', marginBottom: '1rem' }}>📊 Reports</h4>
              
              <button
                onClick={() => triggerBotAction('sales-report')}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  marginBottom: '0.5rem',
                  background: '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                📈 Generate Sales Report
              </button>
              
              <button
                onClick={() => triggerBotAction('export-orders')}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  marginBottom: '0.5rem',
                  background: '#17a2b8',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                📋 Export Orders
              </button>
              
              <button
                onClick={() => triggerBotAction('pdf-generation')}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: '#fd7e14',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                📄 Generate PDFs
              </button>
            </div>

            {/* Maintenance */}
            <div style={{ padding: '1.5rem', border: '1px solid #dee2e6', borderRadius: '8px' }}>
              <h4 style={{ color: '#6c757d', marginBottom: '1rem' }}>🔧 Maintenance</h4>
              
              <button
                onClick={() => triggerBotAction('cache-clear')}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  marginBottom: '0.5rem',
                  background: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                🗑️ Clear Cache
              </button>
              
              <button
                onClick={() => triggerBotAction('health-check')}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  marginBottom: '0.5rem',
                  background: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                🏥 Health Check
              </button>
              
              <button
                onClick={() => triggerBotAction('restart-services')}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: '#ffc107',
                  color: 'black',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                🔄 Restart Services
              </button>
            </div>
          </div>

          <div style={{ marginTop: '2rem', padding: '1rem', background: '#fff3cd', borderRadius: '8px', border: '1px solid #ffeaa7' }}>
            <h5 style={{ color: '#856404', margin: '0 0 0.5rem 0' }}>⚠️ Important Notes</h5>
            <ul style={{ margin: 0, paddingLeft: '1.5rem', color: '#856404' }}>
              <li>Daily pipeline runs automatically at 4:30 AM</li>
              <li>Order monitoring runs every 5 minutes</li>
              <li>Sales reports are generated daily at 6:00 PM</li>
              <li>Manual triggers will execute immediately</li>
            </ul>
          </div>
        </div>
      )}

      {/* Notifications Tab */}
      {activeTab === 'notifications' && (
        <div style={{ background: 'white', padding: '2rem', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
          <h3>🔔 Notification Settings</h3>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '2rem', marginTop: '1.5rem' }}>
            <div>
              <h4>📱 WhatsApp Notifications</h4>
              <div style={{ space: '0.5rem' }}>
                <p><span style={{ color: '#28a745' }}>✅</span> New Orders</p>
                <p><span style={{ color: '#28a745' }}>✅</span> Cancelled Orders</p>
                <p><span style={{ color: '#28a745' }}>✅</span> Daily Reports</p>
                <p><span style={{ color: '#6c757d' }}>❌</span> System Alerts</p>
              </div>
            </div>
            
            <div>
              <h4>📧 Email Notifications</h4>
              <div style={{ space: '0.5rem' }}>
                <p><span style={{ color: '#28a745' }}>✅</span> PDF Reports</p>
                <p><span style={{ color: '#28a745' }}>✅</span> Sales Summary</p>
                <p><span style={{ color: '#6c757d' }}>❌</span> Error Alerts</p>
                <p><span style={{ color: '#6c757d' }}>❌</span> Weekly Reports</p>
              </div>
            </div>
            
            <div>
              <h4>💬 Telegram Notifications</h4>
              <div style={{ space: '0.5rem' }}>
                <p><span style={{ color: '#28a745' }}>✅</span> System Status</p>
                <p><span style={{ color: '#28a745' }}>✅</span> Error Alerts</p>
                <p><span style={{ color: '#6c757d' }}>❌</span> Order Updates</p>
                <p><span style={{ color: '#6c757d' }}>❌</span> Performance Metrics</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Account Tab */}
      {activeTab === 'account' && (
        <div style={{ background: 'white', padding: '2rem', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
          <h3>👤 Account Information</h3>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '2rem', marginTop: '1.5rem' }}>
            <div>
              <h4>Profile Details</h4>
              <div style={{ space: '0.5rem' }}>
                <p><strong>Name:</strong> {user?.fullName}</p>
                <p><strong>Email:</strong> {user?.primaryEmailAddress?.emailAddress}</p>
                <p><strong>Role:</strong> Administrator</p>
                <p><strong>Last Login:</strong> {new Date().toLocaleString()}</p>
              </div>
            </div>
            
            <div>
              <h4>Security</h4>
              <div style={{ space: '0.5rem' }}>
                <p><span style={{ color: '#28a745' }}>✅</span> Two-Factor Authentication</p>
                <p><span style={{ color: '#28a745' }}>✅</span> Email Verified</p>
                <p><span style={{ color: '#28a745' }}>✅</span> Strong Password</p>
                <p><span style={{ color: '#28a745' }}>✅</span> Secure Session</p>
              </div>
            </div>
          </div>

          <div style={{ marginTop: '2rem', padding: '1rem', background: '#f8f9fa', borderRadius: '8px' }}>
            <h5>🔐 Session Management</h5>
            <p style={{ color: '#6c757d', margin: '0.5rem 0' }}>
              Your session is secured with Clerk authentication. You can manage your account settings 
              and security preferences through the Clerk dashboard.
            </p>
            <button
              onClick={() => window.open('https://dashboard.clerk.com', '_blank')}
              style={{
                padding: '0.5rem 1rem',
                background: '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              🔗 Open Clerk Dashboard
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
